/**
 * Cart drawer helpers.
 *
 * Closes the drawer from the page overlay. Horizon already closes on
 * Escape and the X control. Under 990px the dialog is modal and the
 * native backdrop does the same job. The overlay covers the desktop
 * sidebar, which has no backdrop.
 *
 * Also drops the theme's fly-to-cart and button checkmark. The header
 * cart icon pulse is driven from cart-icon.js when the count updates.
 */
(() => {
  const TRADE_ENDPOINT = 'https://br-noisy-union-b94v25a9-cfsupport.compute.c-7.eu-central-1.aws.neon.tech/trade-signup';
  const TRADE_PENDING_KEY = 'cfTradePending';
  const REOPEN_KEY = 'cfReopenCart';

  const closeFromBackdrop = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-cf-cart-backdrop]')) return;

    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  document.addEventListener('click', closeFromBackdrop);

  const labelCheckout = () => {
    const buttons = document.querySelectorAll('#cart-drawer .cart__checkout-button .button-text');
    buttons.forEach((button) => {
      if (button.textContent.trim() === 'Checkout') return;
      button.textContent = 'Checkout';
    });
  };

  const markExpressRow = (root) => {
    const scope = root instanceof Element ? root : document;
    const rows = scope.id === 'cart-drawer'
      ? scope.querySelectorAll('.additional-checkout-buttons')
      : scope.querySelectorAll('#cart-drawer .additional-checkout-buttons');
    rows.forEach((row) => {
      row.classList.add('additional-checkout-buttons--horizontal');
    });
  };

  const watchCheckoutLabel = () => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    labelCheckout();
    markExpressRow(drawer);
    const observer = new MutationObserver(() => {
      labelCheckout();
      markExpressRow(drawer);
    });
    observer.observe(drawer, { childList: true, subtree: true, characterData: true });
  };

  const patchExpressRow = () => {
    const ctor = customElements.get('shopify-accelerated-checkout-cart');
    if (!ctor || ctor.prototype.cfRowPatched) return Boolean(ctor);
    const original = ctor.prototype.connectedCallback;
    ctor.prototype.cfRowPatched = true;
    ctor.prototype.connectedCallback = function connectedCallback() {
      const wrap = this.closest('#cart-drawer .additional-checkout-buttons');
      if (wrap) wrap.classList.add('additional-checkout-buttons--horizontal');
      if (typeof original === 'function') return original.apply(this, arguments);
      return undefined;
    };
    return true;
  };

  if (!patchExpressRow()) {
    customElements.whenDefined('shopify-accelerated-checkout-cart').then(patchExpressRow);
  }

  const silenceMotion = () => {
    const ctor = customElements.get('add-to-cart-component');
    if (!ctor || ctor.prototype.cfMotionSilenced) return Boolean(ctor);

    ctor.prototype.cfMotionSilenced = true;
    ctor.prototype.handleClick = function handleClick() {};
    ctor.prototype.animateAddToCart = function animateAddToCart() {};
    return true;
  };

  if (!silenceMotion()) {
    customElements.whenDefined('add-to-cart-component').then(silenceMotion);
  }

  const openCartDrawer = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    customElements.whenDefined('theme-drawer').then(() => {
      const ready = document.getElementById('cart-drawer');
      if (ready && typeof ready.open === 'function') ready.open();
    });
  };

  const reopenCartAfterLogin = () => {
    const url = new URL(window.location.href);
    let flagged = url.searchParams.get('cf_cart') === 'open';
    try {
      if (window.sessionStorage.getItem(REOPEN_KEY) === '1') flagged = true;
      window.sessionStorage.removeItem(REOPEN_KEY);
    } catch (error) {
      /* sessionStorage can be blocked */
    }
    if (!flagged || window.top !== window.self) return;
    url.searchParams.delete('cf_cart');
    const next = url.pathname + url.search + url.hash;
    window.history.replaceState({}, '', next);
    openCartDrawer();
  };

  const loginHref = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('cf_cart', 'open');
    const returnTo = url.pathname + url.search + url.hash;
    return '/customer_authentication/login?locale=en&return_to=' + encodeURIComponent(returnTo);
  };

  const rememberReopen = () => {
    try {
      window.sessionStorage.setItem(REOPEN_KEY, '1');
    } catch (error) {
      /* sessionStorage can be blocked; return_to still reopens the drawer */
    }
  };

  const ensureModal = () => {
    let modal = document.getElementById('cf-trade-modal');
    if (modal) return modal;
    modal = document.createElement('dialog');
    modal.id = 'cf-trade-modal';
    modal.className = 'cf-trade-modal';
    modal.innerHTML =
      '<div class="cf-trade-modal__head">' +
      '<h2 class="cf-trade-modal__title" id="cf-trade-modal-title"></h2>' +
      '<button type="button" class="cf-trade-modal__close" data-cf-trade-close aria-label="Close">' +
      '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '</svg></button></div>' +
      '<div class="cf-trade-modal__body" data-cf-trade-body></div>';
    modal.addEventListener('click', (event) => {
      if (event.target === modal) modal.close();
    });
    modal.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-cf-trade-close]')) return;
      modal.close();
    });
    document.body.appendChild(modal);
    return modal;
  };

  const signupSrc = () => {
    const src = new URL('/pages/trade-signup', window.location.origin);
    src.searchParams.set('cf_embed', '1');
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) src.searchParams.set('preview_theme_id', preview);
    return src.toString();
  };

  const openTradeModal = (mode) => {
    const modal = ensureModal();
    const title = modal.querySelector('#cf-trade-modal-title');
    const body = modal.querySelector('[data-cf-trade-body]');
    if (!title || !body) return;
    if (mode === 'signin') {
      title.textContent = 'Sign in for trade prices';
      body.innerHTML =
        '<p>Sign-in is hosted by Shopify, so it can’t open inside this cart. Continue to Shopify’s secure sign-in and you’ll come back to this same page with the cart drawer open. Trade prices apply when the account is tagged trade.</p>' +
        '<a class="cf-trade-modal__continue" data-cf-trade-continue href="">Continue to sign in</a>';
      const link = body.querySelector('[data-cf-trade-continue]');
      if (link) {
        link.setAttribute('href', loginHref());
        link.addEventListener('click', rememberReopen);
      }
    } else {
      title.textContent = 'Sign up for trade prices';
      body.innerHTML =
        '<p class="cf-trade-modal__status">Loading the trade form…</p>' +
        '<iframe class="cf-trade-modal__frame" title="Trade signup" src=""></iframe>';
      const frame = body.querySelector('iframe');
      if (frame) frame.src = signupSrc();
    }
    if (!modal.open && typeof modal.showModal === 'function') modal.showModal();
    const close = modal.querySelector('[data-cf-trade-close]');
    if (close instanceof HTMLElement) close.focus();
  };

  if (!window.cfCartExpress) {
    window.cfCartExpress = true;
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const signin = target.closest('#cart-drawer .cf-cart-trade__signin');
      const signup = target.closest('#cart-drawer .cf-cart-trade__signup');
      if (!signin && !signup) return;
      event.preventDefault();
      openTradeModal(signin ? 'signin' : 'signup');
    });
  }

  const showEmbedThanks = (form) => {
    const note = document.createElement('div');
    note.className = 'cf-ts__ok';
    note.setAttribute('role', 'status');
    note.innerHTML = '<p>Thanks for signing up. See our <a href="/pages/subscription-services">subscription services page</a> for an overview of the SIM cards and software to enhance any ControlFreq system.</p><a class="btng" href="/pages/subscription-services">View subscription services</a>';
    form.replaceWith(note);
  };

  const postTradeCopy = () => {
    try {
      const raw = window.sessionStorage.getItem(TRADE_PENDING_KEY);
      window.sessionStorage.removeItem(TRADE_PENDING_KEY);
      if (!raw || !window.fetch) return;
      window.fetch(TRADE_ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: raw,
      }).catch(() => {});
    } catch (error) {
      /* never block the shopper on the copy */
    }
  };

  const bindEmbedSignup = () => {
    if (window.top === window.self) return;
    if (!document.documentElement.classList.contains('cf-embed')) return;
    const form = document.querySelector('form.cf-ts__form');
    if (!(form instanceof HTMLFormElement) || form.dataset.cfAjax === '1') return;
    form.dataset.cfAjax = '1';
    form.addEventListener('submit', async (event) => {
      if (form.dataset.cfNative === '1') return;
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      const hasTurnstile = Boolean(form.querySelector('.cf-turnstile'));
      const tokenField = form.querySelector('[name="cf-turnstile-response"]');
      const token = tokenField instanceof HTMLInputElement ? tokenField.value.trim() : '';
      if (hasTurnstile && !token) return;

      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.textContent = 'Sending application…';
      }

      const fallback = () => {
        form.dataset.cfNative = '1';
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.textContent = 'Send application';
        }
        form.submit();
      };

      try {
        const response = await window.fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          redirect: 'follow',
          headers: { Accept: 'text/html' },
        });
        const html = await response.text();
        const challenge = response.status === 403 || html.includes('Verifying your connection');
        if (challenge) {
          fallback();
          return;
        }
        if (html.includes('cf-ts__ok') || html.includes('your application is in')) {
          showEmbedThanks(form);
          postTradeCopy();
          return;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const errors = doc.querySelector('.errors, .form-status');
        if (errors) {
          const existing = form.querySelector('.cf-ts__ajax-error');
          if (existing) existing.remove();
          const note = document.createElement('div');
          note.className = 'cf-ts__ajax-error';
          note.textContent = errors.textContent.replace(/\s+/g, ' ').trim();
          form.prepend(note);
          if (button instanceof HTMLButtonElement) {
            button.disabled = false;
            button.textContent = 'Send application';
          }
          return;
        }
        fallback();
      } catch (error) {
        fallback();
      }
    });
  };

  const boot = () => {
    watchCheckoutLabel();
    reopenCartAfterLogin();
    bindEmbedSignup();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
